import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { create } from 'jss';
import { responsiveFontSizes, adaptV4Theme } from '@mui/material';
import {
  createTheme as createMuiTheme,
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import StylesProvider from '@mui/styles/StylesProvider';
import jssPreset from '@mui/styles/jssPreset';
import { themeConfigV4 } from './theme/v4';
import { getThemeConfig } from './theme/v5';
import { getCompanyTheme } from '../lib/appsRegistry';
import useSettings from '../../hooks/useSettings';

const createThemeProvider = async companyName => {
  const jss = create({ plugins: [...jssPreset().plugins] });
  const themeData = await getCompanyTheme({ companyName });
  const {
    themeConfigs: compThemeConfigs,
    themeOverrides: compThemeOverrides,
  } = themeData;

  const themeConfigs = [...themeConfigV4, ...compThemeConfigs];

  // DEPRECATED: Rather make theme modifications in "createTheme" using MUI 5 theme structure
  // This remains as the old MUI 4 themes are ported into MUI 5
  const createThemeV4 = (settings = {}) => {
    let themeConfig = themeConfigs.find(
      newTheme => newTheme.name === settings.theme
    );

    if (!themeConfig) {
      console.warn(new Error(`The theme ${settings.theme} is not valid`));
      [themeConfig] = themeConfigs;
    }

    let companyThemeConfig = {};
    if (compThemeOverrides) {
      companyThemeConfig = compThemeOverrides.find(
        newTheme => newTheme.name === settings.theme
      );
    }
    companyThemeConfig = companyThemeConfig || {};

    let theme = createMuiTheme(
      _.merge({}, themeConfig, companyThemeConfig, {
        direction: settings.direction,
      })
    );

    if (settings.responsiveFontSizes) {
      theme = responsiveFontSizes(theme);
    }

    return theme;
  };

  const createTheme = (v4settings = {}) => {
    // We get the old mui 4 theme and port it to mui 5 to use as a base
    const theme = adaptV4Theme(createThemeV4(v4settings));

    return createMuiTheme(theme, getThemeConfig(theme));
  };

  return props => {
    const { settings } = useSettings();
    // eslint-disable-next-line react/prop-types
    const { children } = props;

    return (
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={createTheme(settings)}>
          <StylesProvider jss={jss}>{children}</StylesProvider>
        </MuiThemeProvider>
      </StyledEngineProvider>
    );
  };
};

createThemeProvider.defaultProps = {
  subdomain: null,
};

createThemeProvider.propTypes = {
  subdomain: PropTypes.string,
};

export default createThemeProvider;
